// src/components/guide_designer/layout.js
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AppContexts } from "../../providers";
import FeatherIcon from "../common/FeatherIcon";
import ImageInput from "../common/ImageInput";
import { postPreviewSettings, listenForPreviewReady } from "./guide_preview/message";
import RevisionControls from "./revision-controls";
import {
  GUIDE_THEME_PRESETS,
  GUIDE_THEME_VERSION,
  getGuideThemePreset,
  isGuideThemeId,
  readableTextColor,
  resolveGuideTheme,
} from "./theme-presets";

const DEFAULT_THEME = {
  guestbookHeadline: "",
  yourStayLabel: "Your Stay",
  homeGuideLabel: "Home Guide",
  areaGuideLabel: "Area Guide",
  extrasGuideLabel: "Extras",
  yourStayIntroText: "",
  homeGuideIntroText: "",
  areaGuideIntroText: "",
  extrasGuideIntroText: "",
  hideGuideLogo: false,
  activeTabColor: "#e66656",
  tabTextColor: "#ffffff",
  headerBackgroundColor: "transparent",
  headerImagePosition: "center center",
  pageBackgroundColor: "#f5f5f5",
  distanceColor: "#e66656",

  guideTitleFont: "'Inter', sans-serif",
  guideTitleFontSize: "20px",
  guideTitleFontWeight: "normal",
  guideTitleColor: "#222222",

  sectionTitleFont: "'Inter', sans-serif",
  sectionTitleFontSize: "20px",
  sectionTitleFontWeight: "normal",
  sectionTitleColor: "#222222",

  introTextFont: "'Inter', sans-serif",
  introTextFontSize: "15px",
  introTextFontWeight: "normal",
  introTextColor: "#333333",

  blockStyle: "Collapsible", // "Collapsible" | "Inline"
  blockBorder: true,
  blockBackgroundColor: "#ffffff",

  blockTitleFont: "'Inter', sans-serif",
  blockTitleFontSize: "15px",
  blockTitleFontWeight: "normal",
  blockTitleColor: "#111111",

  blockTextFont: "'Inter', sans-serif",
  blockTextFontSize: "15px",
  blockTextFontWeight: "normal",
  blockTextColor: "#333333",

  areaGuideView: "list",
  guestAiName: "Guide Assistant",
  askRueEnabled: true,
  aiTripPlannerEnabled: true,

  showIcons: true,
  iconColor: "#243e56",
};

const DEFAULT_AREA_GUIDE_SETTINGS = {
  tip_title: "Your host says",
  show_guide_distance: false,
  show_guide_home: true,
};

const VISUAL_THEME_KEYS = new Set([
  "activeTabColor",
  "tabTextColor",
  "headerBackgroundColor",
  "pageBackgroundColor",
  "distanceColor",
  "blockStyle",
  "blockBorder",
  "showIcons",
  "iconColor",
  "blockBackgroundColor",
  "guideTitleFont",
  "guideTitleFontSize",
  "guideTitleFontWeight",
  "guideTitleColor",
  "sectionTitleFont",
  "sectionTitleFontSize",
  "sectionTitleFontWeight",
  "sectionTitleColor",
  "introTextFont",
  "introTextFontSize",
  "introTextFontWeight",
  "introTextColor",
  "blockTitleFont",
  "blockTitleFontSize",
  "blockTitleFontWeight",
  "blockTitleColor",
  "blockTextFont",
  "blockTextFontSize",
  "blockTextFontWeight",
  "blockTextColor",
]);

const buildAreaGuideSettings = (destination = {}) => ({
  ...DEFAULT_AREA_GUIDE_SETTINGS,
  ...destination,
});

const normalizeBlockStyle = (value) => {
  return value === "Inline" ? "Inline" : "Collapsible";
};

const buildThemeFromGuestbook = (guestbook = {}) => ({
  ...DEFAULT_THEME,
  guestbookHeadline:
    guestbook.guestbook_headline || DEFAULT_THEME.guestbookHeadline,
  yourStayLabel:
    guestbook.your_stay_label || DEFAULT_THEME.yourStayLabel,
  homeGuideLabel:
    guestbook.home_guide_label || DEFAULT_THEME.homeGuideLabel,
  areaGuideLabel:
    guestbook.area_guide_label || DEFAULT_THEME.areaGuideLabel,
  extrasGuideLabel:
    guestbook.extras_guide_label || DEFAULT_THEME.extrasGuideLabel,
  yourStayIntroText:
    guestbook.your_stay_intro_text || DEFAULT_THEME.yourStayIntroText,
  homeGuideIntroText:
    guestbook.home_guide_intro_text || DEFAULT_THEME.homeGuideIntroText,
  areaGuideIntroText:
    guestbook.area_guide_intro_text || DEFAULT_THEME.areaGuideIntroText,
  extrasGuideIntroText:
    guestbook.extras_guide_intro_text || DEFAULT_THEME.extrasGuideIntroText,
  hideGuideLogo:
    guestbook.hide_logo_on_guide ?? DEFAULT_THEME.hideGuideLogo,
  activeTabColor:
    guestbook.active_tab_color || DEFAULT_THEME.activeTabColor,
  tabTextColor: guestbook.tab_text_color || DEFAULT_THEME.tabTextColor,
  headerBackgroundColor:
    guestbook.header_background_color ?? DEFAULT_THEME.headerBackgroundColor,
  headerImagePosition:
    guestbook.header_image_position || DEFAULT_THEME.headerImagePosition,
  pageBackgroundColor:
    guestbook.page_background_color || DEFAULT_THEME.pageBackgroundColor,
  distanceColor: guestbook.distance_color || DEFAULT_THEME.distanceColor,
  blockStyle: normalizeBlockStyle(guestbook.block_style),
  blockBorder: guestbook.block_border ?? DEFAULT_THEME.blockBorder,
  showIcons: guestbook.show_icons ?? DEFAULT_THEME.showIcons,
  iconColor: guestbook.icon_color || DEFAULT_THEME.iconColor,
  blockBackgroundColor:
    guestbook.block_background_color || DEFAULT_THEME.blockBackgroundColor,
  guideTitleFont:
    guestbook.guide_title_font ||
    guestbook.section_title_font ||
    DEFAULT_THEME.guideTitleFont,
  guideTitleFontSize:
    guestbook.guide_title_font_size ||
    guestbook.section_title_font_size ||
    DEFAULT_THEME.guideTitleFontSize,
  guideTitleFontWeight:
    guestbook.guide_title_font_weight ||
    guestbook.section_title_font_weight ||
    DEFAULT_THEME.guideTitleFontWeight,
  guideTitleColor:
    guestbook.guide_title_color ||
    guestbook.section_title_color ||
    DEFAULT_THEME.guideTitleColor,
  sectionTitleFont:
    guestbook.section_title_font || DEFAULT_THEME.sectionTitleFont,
  sectionTitleFontSize:
    guestbook.section_title_font_size || DEFAULT_THEME.sectionTitleFontSize,
  sectionTitleFontWeight:
    guestbook.section_title_font_weight ||
    DEFAULT_THEME.sectionTitleFontWeight,
  sectionTitleColor:
    guestbook.section_title_color || DEFAULT_THEME.sectionTitleColor,
  introTextFont: guestbook.intro_text_font || DEFAULT_THEME.introTextFont,
  introTextFontSize:
    guestbook.intro_text_font_size || DEFAULT_THEME.introTextFontSize,
  introTextFontWeight:
    guestbook.intro_text_font_weight || DEFAULT_THEME.introTextFontWeight,
  introTextColor:
    guestbook.intro_text_color || DEFAULT_THEME.introTextColor,
  blockTitleFont:
    guestbook.block_title_font || DEFAULT_THEME.blockTitleFont,
  blockTitleFontSize:
    guestbook.block_title_font_size || DEFAULT_THEME.blockTitleFontSize,
  blockTitleFontWeight:
    guestbook.block_title_font_weight || DEFAULT_THEME.blockTitleFontWeight,
  blockTitleColor:
    guestbook.block_title_color || DEFAULT_THEME.blockTitleColor,
  blockTextFont: guestbook.block_text_font || DEFAULT_THEME.blockTextFont,
  blockTextFontSize:
    guestbook.block_text_font_size || DEFAULT_THEME.blockTextFontSize,
  blockTextFontWeight:
    guestbook.block_text_font_weight || DEFAULT_THEME.blockTextFontWeight,
  blockTextColor: guestbook.block_text_color || DEFAULT_THEME.blockTextColor,
  areaGuideView: guestbook.area_guide_view || DEFAULT_THEME.areaGuideView,
  guestAiName: guestbook.guest_ai_name || DEFAULT_THEME.guestAiName,
  askRueEnabled:
    guestbook.ask_rue_enabled ?? DEFAULT_THEME.askRueEnabled,
  aiTripPlannerEnabled:
    guestbook.ai_trip_planner_enabled ?? DEFAULT_THEME.aiTripPlannerEnabled,
});

const buildDesignerTheme = (guestbook = {}) => {
  const legacyTheme = buildThemeFromGuestbook(guestbook);
  if (!isGuideThemeId(guestbook.theme_id)) return legacyTheme;

  const presetTheme = resolveGuideTheme(
    guestbook.theme_id,
    guestbook.brand_accent,
  );

  return {
    ...legacyTheme,
    ...presetTheme,
    blockBorder: guestbook.block_border ?? presetTheme.blockBorder,
  };
};

const buildGuestbookFromTheme = (
  guestbook = {},
  theme = {},
  layoutStyle,
  themeMeta = {},
) => ({
  ...guestbook,
  guestbook_headline: theme.guestbookHeadline,
  your_stay_label: theme.yourStayLabel,
  home_guide_label: theme.homeGuideLabel,
  area_guide_label: theme.areaGuideLabel,
  extras_guide_label: theme.extrasGuideLabel,
  your_stay_intro_text: theme.yourStayIntroText,
  home_guide_intro_text: theme.homeGuideIntroText,
  area_guide_intro_text: theme.areaGuideIntroText,
  extras_guide_intro_text: theme.extrasGuideIntroText,
  hide_logo_on_guide: theme.hideGuideLogo,
  layout_style: layoutStyle,
  active_tab_color: theme.activeTabColor,
  tab_text_color: theme.tabTextColor,
  header_background_color: theme.headerBackgroundColor,
  header_image_position: theme.headerImagePosition,
  page_background_color: theme.pageBackgroundColor,
  distance_color: theme.distanceColor,
  block_style: normalizeBlockStyle(theme.blockStyle),
  block_border: theme.blockBorder,
  show_icons: theme.showIcons,
  icon_color: theme.iconColor,
  block_background_color: theme.blockBackgroundColor,
  guide_title_font: theme.guideTitleFont,
  guide_title_font_size: theme.guideTitleFontSize,
  guide_title_font_weight: theme.guideTitleFontWeight,
  guide_title_color: theme.guideTitleColor,
  section_title_font: theme.sectionTitleFont,
  section_title_font_size: theme.sectionTitleFontSize,
  section_title_font_weight: theme.sectionTitleFontWeight,
  section_title_color: theme.sectionTitleColor,
  intro_text_font: theme.introTextFont,
  intro_text_font_size: theme.introTextFontSize,
  intro_text_font_weight: theme.introTextFontWeight,
  intro_text_color: theme.introTextColor,
  block_title_font: theme.blockTitleFont,
  block_title_font_size: theme.blockTitleFontSize,
  block_title_font_weight: theme.blockTitleFontWeight,
  block_title_color: theme.blockTitleColor,
  block_text_font: theme.blockTextFont,
  block_text_font_size: theme.blockTextFontSize,
  block_text_font_weight: theme.blockTextFontWeight,
  block_text_color: theme.blockTextColor,
  area_guide_view: theme.areaGuideView,
  guest_ai_name: theme.guestAiName,
  ask_rue_enabled: theme.askRueEnabled,
  ai_trip_planner_enabled: theme.aiTripPlannerEnabled,
  theme_id: themeMeta.themeId || null,
  theme_version: themeMeta.themeId ? GUIDE_THEME_VERSION : 1,
  brand_accent: themeMeta.brandAccent || null,
});

const FONT_OPTIONS = [
  { label: "Inter", value: "'Inter', sans-serif" },
  { label: "Lato", value: "'Lato', sans-serif" },
  { label: "Roboto", value: "'Roboto', sans-serif" },
  { label: "Oswald", value: "'Oswald', sans-serif" },
  { label: "Poppins", value: "'Poppins', sans-serif" },
  { label: "Playfair Display", value: "'Playfair Display', serif" },
  { label: "Sans-serif", value: "sans-serif" },
  { label: "Serif", value: "serif" },
];

const FontPreview = ({ fontFamily, fontSize, fontWeight, color }) => {
  return (
    <span
      className="guide-layout-aa"
      style={{
        "--guide-layout-preview-font-family": fontFamily,
        "--guide-layout-preview-font-size": fontSize,
        "--guide-layout-preview-font-weight": fontWeight,
        "--guide-layout-preview-color": color,
      }}
    >
      Aa
    </span>
  );
};

const ToggleField = ({ id, label, checked, onChange }) => (
  <div className="guide-layout-field guide-layout-toggle rbr-checkbox">
    <label className="uk-form-label guide-layout-toggle-label" htmlFor={id}>
      <span className="setting-switch">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
        />
        <span className="setting-slider setting-round" />
      </span>
      <span>{label}</span>
    </label>
  </div>
);

const LAYOUT_ACCORDION_STORAGE_KEY = "guideDesigner.layoutAccordions.v1";
const DEFAULT_LAYOUT_ACCORDIONS = {
  header: true,
  themes: true,
  colors: false,
  typography: false,
  block: false,
  area: false,
};

const loadLayoutAccordions = (expandAll = false) => {
  if (expandAll) {
    return Object.fromEntries(
      Object.keys(DEFAULT_LAYOUT_ACCORDIONS).map((key) => [key, true]),
    );
  }

  try {
    const saved = JSON.parse(
      window.sessionStorage.getItem(LAYOUT_ACCORDION_STORAGE_KEY),
    );
    return { ...DEFAULT_LAYOUT_ACCORDIONS, ...(saved || {}) };
  } catch {
    return { ...DEFAULT_LAYOUT_ACCORDIONS };
  }
};

const LayoutAccordionSection = ({
  id,
  title,
  expanded,
  onToggle,
  children,
  className = "",
  hidden = false,
}) => {
  const contentId = `guide-layout-${id}-content`;

  return (
    <section
      className={`guide-layout-section guide-layout-accordion ${className}`.trim()}
      hidden={hidden}
    >
      <button
        type="button"
        className="guide-layout-accordion-trigger"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <span className="guide-layout-section-title">{title}</span>
        <FeatherIcon name={expanded ? "chevron-up" : "chevron-down"} />
      </button>
      <div
        id={contentId}
        className="guide-layout-accordion-content"
        hidden={!expanded}
      >
        {children}
      </div>
    </section>
  );
};

const ColorField = ({
  label,
  value,
  onChange,
  onReset,
  placeholder,
  fallback,
}) => {
  return (
    <div className="guide-color-field">
      <label className="uk-form-label">{label}</label>
      <div className="uk-form-controls setting-color-box">
        <div className="guide-color-control">
          <div
            className="rbr-color-preview"
            style={{
              backgroundColor: validateColor(value),
            }}
          >
            <input
              type="color"
              value={getColorInputValue(value, fallback)}
              onChange={(e) => onChange(e.target.value)}
              aria-label={`Choose ${label}`}
            />
          </div>
          <input
            className="setting-color-value"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || fallback}
            aria-label={`${label} value`}
          />
        </div>
      </div>
      <button
        type="button"
        className="uk-button resetOldColor"
        onClick={onReset}
      >
        Reset
      </button>
    </div>
  );
};

const SIZE_OPTIONS = [
  "12px",
  "13px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
];

const WEIGHT_OPTIONS = [
  { label: "Normal", value: "normal" },
  { label: "Bold", value: "bold" },
];

const HEADER_IMAGE_POSITION_OPTIONS = [
  { label: "Top left", value: "left top" },
  { label: "Top center", value: "center top" },
  { label: "Top right", value: "right top" },
  { label: "Center left", value: "left center" },
  { label: "Center", value: "center center" },
  { label: "Center right", value: "right center" },
  { label: "Bottom left", value: "left bottom" },
  { label: "Bottom center", value: "center bottom" },
  { label: "Bottom right", value: "right bottom" },
];

const AUTOSAVE_DELAY_MS = 700;
const IMAGE_AUTOSAVE_DELAY_MS = 100;
const EMPTY_ATTACHMENTS = [];
const GUIDEBOOK_IMAGE_TYPES = new Set([
  "guestbook_header_image",
  "guestbook_image",
]);

const getColorInputValue = (value, fallback = "#ffffff") => {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value || "")
    ? value
    : fallback;
};

const validateColor = (color) => {
  const style = new Option().style;
  style.color = color;
  return style.color !== "" ? color : "rgba(0, 0, 0, 0)";
};

const mergeGuestbookAttachments = (...attachmentLists) => {
  const merged = [];

  attachmentLists.flat().forEach((attachment) => {
    if (!attachment) {
      return;
    }

    const key = attachment.id || `${attachment.type}:${attachment.url || ""}`;
    if (!merged.find((item) => (item.id || `${item.type}:${item.url || ""}`) === key)) {
      merged.push(attachment);
    }
  });

  return merged;
};

const BuilderLayoutPanel = ({
  onSaved,
  onboardingFocus = "",
  panelFocus = "",
  hideRevisionControls = false,
  onRegisterSave,
}) => {
  const { settings, set, setSettings, get, loading } = useContext(
    AppContexts.SettingsContext,
  );
  const { user } = useContext(AppContexts.AuthContext);
  const { uploadFile } = useContext(AppContexts.UtilContext);
  const isAiPanel = panelFocus === "ai";

  // Lazy-load theme/layout settings on mount.
  useEffect(() => {
    if (
      settings?.guestbook === undefined ||
      settings?.destination === undefined ||
      settings?.branding === undefined
    ) {
      get(["guestbook", "destination", "branding"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local edits update the preview immediately and persist as a draft shortly
  // after the user stops changing controls.
  const [draftTheme, setDraftTheme] = useState(() =>
    buildDesignerTheme(settings?.guestbook),
  );
  const [draftThemeId, setDraftThemeId] = useState(() =>
    isGuideThemeId(settings?.guestbook?.theme_id)
      ? settings.guestbook.theme_id
      : null,
  );
  const [draftBrandAccent, setDraftBrandAccent] = useState(
    () =>
      settings?.guestbook?.brand_accent ||
      settings?.guestbook?.active_tab_color ||
      "#2563eb",
  );
  const [draftLayout, setDraftLayout] = useState(
    () => settings?.guestbook?.layout_style || "tabs",
  );
  const [draftAreaGuide, setDraftAreaGuide] = useState(() =>
    buildAreaGuideSettings(settings?.destination),
  );
  const [draftLogoUrl, setDraftLogoUrl] = useState(
    () => settings?.branding?.logo_url || "",
  );
  const [savedAt, setSavedAt] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [hasHydrated, setHasHydrated] = useState(
    settings?.guestbook !== undefined,
  );
  const [defaultImageUpload, setDefaultImageUpload] = useState(null);
  const [defaultImageRemoved, setDefaultImageRemoved] = useState(false);
  const [headerImageUpload, setHeaderImageUpload] = useState(null);
  const [headerImageRemoved, setHeaderImageRemoved] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState(() =>
    loadLayoutAccordions(!!onboardingFocus),
  );
  const hydratedSettingsRef = useRef({});
  const persistLayoutRef = useRef(null);
  const pendingLayoutRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const savedDraftRef = useRef(null);
  const queuedDraftRef = useRef(null);
  const defaultImageRevisionRef = useRef(0);
  const headerImageRevisionRef = useRef(0);

  useEffect(() => {
    if (onboardingFocus) return;

    try {
      window.sessionStorage.setItem(
        LAYOUT_ACCORDION_STORAGE_KEY,
        JSON.stringify(expandedAccordions),
      );
    } catch {
      // The accordion still works when session storage is unavailable.
    }
  }, [expandedAccordions, onboardingFocus]);

  const toggleLayoutAccordion = (id) => {
    setExpandedAccordions((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  // Server responses update the saved baseline, never a newer local draft.
  // Each settings group may arrive separately during the initial load.
  useEffect(() => {
    if (settings?.guestbook === undefined || hydratedSettingsRef.current.guestbook) return;
    hydratedSettingsRef.current.guestbook = true;
    setDraftTheme(buildDesignerTheme(settings?.guestbook));
    setDraftThemeId(
      isGuideThemeId(settings.guestbook.theme_id)
        ? settings.guestbook.theme_id
        : null,
    );
    setDraftBrandAccent(
      settings.guestbook.brand_accent ||
        settings.guestbook.active_tab_color ||
        getGuideThemePreset(settings.guestbook.theme_id)?.defaultAccent ||
        "#2563eb",
    );
    setDraftLayout(settings?.guestbook?.layout_style || "tabs");
    setHasHydrated(true);
  }, [settings?.guestbook]);

  useEffect(() => {
    if (settings?.destination === undefined || hydratedSettingsRef.current.destination) return;
    hydratedSettingsRef.current.destination = true;
    setDraftAreaGuide(buildAreaGuideSettings(settings.destination));
  }, [settings?.destination]);

  useEffect(() => {
    if (settings?.branding === undefined || hydratedSettingsRef.current.branding) return;
    hydratedSettingsRef.current.branding = true;
    setDraftLogoUrl(settings.branding.logo_url || "");
  }, [settings?.branding]);

  const savedTheme = useMemo(
    () => buildDesignerTheme(settings?.guestbook),
    [settings?.guestbook],
  );
  const savedAreaGuide = useMemo(
    () => buildAreaGuideSettings(settings?.destination),
    [settings?.destination],
  );
  const savedLogoUrl = settings?.branding?.logo_url || "";
  const savedLogoImageUrl = settings?.branding?.logo_image_url || "";

  const isAreaGuideDirty = useMemo(
    () =>
      Object.keys(DEFAULT_AREA_GUIDE_SETTINGS).some(
        (key) => draftAreaGuide[key] !== savedAreaGuide[key],
      ),
    [draftAreaGuide, savedAreaGuide],
  );

  const guestbookAttachments =
    settings?.guestbook?.attachments || EMPTY_ATTACHMENTS;
  const brandingAttachments =
    settings?.branding?.attachments || EMPTY_ATTACHMENTS;
  const allGuestbookAttachments = useMemo(
    () => mergeGuestbookAttachments(guestbookAttachments, brandingAttachments),
    [guestbookAttachments, brandingAttachments],
  );

  const headerImage = useMemo(
    () =>
      allGuestbookAttachments.find(
        (attachment) => attachment.type === "guestbook_header_image",
      ) || null,
    [allGuestbookAttachments],
  );

  const defaultGuestbookImage = useMemo(
    () =>
      allGuestbookAttachments.find(
        (attachment) => attachment.type === "guestbook_image",
      ) || null,
    [allGuestbookAttachments],
  );

  const defaultImagePreviewUrl = useMemo(
    () =>
      defaultImageUpload && typeof URL.createObjectURL === "function"
        ? URL.createObjectURL(defaultImageUpload)
        : null,
    [defaultImageUpload],
  );
  const headerImagePreviewUrl = useMemo(
    () =>
      headerImageUpload && typeof URL.createObjectURL === "function"
        ? URL.createObjectURL(headerImageUpload)
        : null,
    [headerImageUpload],
  );

  useEffect(
    () => () => {
      if (defaultImagePreviewUrl) URL.revokeObjectURL(defaultImagePreviewUrl);
    },
    [defaultImagePreviewUrl],
  );
  useEffect(
    () => () => {
      if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
    },
    [headerImagePreviewUrl],
  );

  // Only local edits identify a new draft. Settings responses and save-status
  // renders must not turn an acknowledged draft into another save operation.
  const defaultImageRevision = defaultImageRevisionRef.current;
  const headerImageRevision = headerImageRevisionRef.current;
  const draft = useMemo(() => ({
    theme: draftTheme,
    themeId: draftThemeId,
    brandAccent: draftBrandAccent,
    layout: draftLayout,
    areaGuide: draftAreaGuide,
    logoUrl: draftLogoUrl,
    defaultImageRevision,
    headerImageRevision,
  }), [draftTheme, draftThemeId, draftBrandAccent, draftLayout, draftAreaGuide,
    draftLogoUrl, defaultImageRevision, headerImageRevision]);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const isDirty = useMemo(() => {
    if (savedDraftRef.current === draft) return false;
    if (draftLogoUrl !== savedLogoUrl) {
      return true;
    }
    if (isAreaGuideDirty) {
      return true;
    }
    if (draftLayout !== (settings?.guestbook?.layout_style || "tabs")) {
      return true;
    }
    if (draftThemeId !== (settings?.guestbook?.theme_id || null)) {
      return true;
    }
    const savedBrandAccent =
      settings?.guestbook?.brand_accent ||
      settings?.guestbook?.active_tab_color ||
      "#2563eb";
    if (draftBrandAccent !== savedBrandAccent) {
      return true;
    }
    if (
      defaultImageUpload !== null ||
      defaultImageRemoved ||
      headerImageUpload !== null ||
      headerImageRemoved
    ) {
      return true;
    }
    return Object.keys(DEFAULT_THEME).some(
      (k) => draftTheme[k] !== savedTheme[k],
    );
  }, [
    draft,
    saveStatus,
    draftTheme,
    draftThemeId,
    draftBrandAccent,
    draftLayout,
    savedTheme,
    settings?.guestbook,
    defaultImageUpload,
    defaultImageRemoved,
    headerImageUpload,
    headerImageRemoved,
    isAreaGuideDirty,
    draftLogoUrl,
    savedLogoUrl,
  ]);

  const previewLogoImageUrl = savedLogoImageUrl;

  const previewGuestbook = useMemo(() => {
    const nextGuestbook = buildGuestbookFromTheme(
      settings?.guestbook,
      draftTheme,
      draftLayout,
      {
        themeId: draftThemeId,
        brandAccent: draftBrandAccent,
      },
    );
    let nextAttachments = allGuestbookAttachments.filter(
      (attachment) => !GUIDEBOOK_IMAGE_TYPES.has(attachment.type),
    );

    if (!defaultImageRemoved) {
      if (defaultImagePreviewUrl) {
        nextAttachments.push({
          type: "guestbook_image",
          name: "guestbook_default_preview",
          url: defaultImagePreviewUrl,
        });
      } else if (defaultGuestbookImage) {
        nextAttachments.push(defaultGuestbookImage);
      }
    }

    if (!headerImageRemoved) {
      if (headerImagePreviewUrl) {
        nextAttachments.push({
          type: "guestbook_header_image",
          name: "guestbook_header_image_preview",
          url: headerImagePreviewUrl,
        });
      } else if (headerImage) {
        nextAttachments.push(headerImage);
      }
    }

    nextGuestbook.attachments = nextAttachments;
    return nextGuestbook;
  }, [
    allGuestbookAttachments,
    defaultGuestbookImage,
    defaultImagePreviewUrl,
    defaultImageRemoved,
    draftLayout,
    draftThemeId,
    draftBrandAccent,
    draftTheme,
    headerImage,
    headerImagePreviewUrl,
    headerImageRemoved,
    settings?.guestbook,
  ]);

  useEffect(() => {
    if (!hasHydrated) return;
    const sendSettings = () => postPreviewSettings(previewGuestbook, {
      destination: draftAreaGuide,
      branding: {
        logo_image_url: previewLogoImageUrl,
        logo_url: draftLogoUrl,
      },
    });
    sendSettings();
    return listenForPreviewReady(sendSettings);
  }, [
    draftAreaGuide,
    draftLogoUrl,
    hasHydrated,
    previewGuestbook,
    previewLogoImageUrl,
  ]);

  // Backwards-compatible local helpers (the rest of the form below uses these).
  const theme = draftTheme;
  const updateTheme = (key, value) => {
    setDraftTheme((prev) => ({ ...prev, [key]: value }));
    if (VISUAL_THEME_KEYS.has(key)) {
      setDraftThemeId(null);
    }
  };
  const toggleThemeBool = (key) => {
    updateTheme(key, !theme[key]);
  };
  const resetThemeValue = (key) => {
    updateTheme(key, DEFAULT_THEME[key]);
  };
  const applyThemePreset = (themeId, options = {}) => {
    const preset = getGuideThemePreset(themeId);
    if (!preset) return;
    const brandAccent = options.brandAccent || preset.defaultAccent;
    const resolved = resolveGuideTheme(themeId, brandAccent);

    setDraftThemeId(themeId);
    setDraftBrandAccent(brandAccent);
    setDraftLayout(preset.layoutStyle);
    setDraftTheme((current) => ({ ...current, ...resolved }));
  };
  const updateBrandAccent = (brandAccent) => {
    setDraftBrandAccent(brandAccent);
    setDraftTheme((current) =>
      draftThemeId
        ? {
            ...current,
            ...resolveGuideTheme(draftThemeId, brandAccent),
          }
        : {
            ...current,
            activeTabColor: brandAccent,
            tabTextColor: readableTextColor(brandAccent),
            distanceColor: brandAccent,
            iconColor: brandAccent,
          },
    );
  };
  const handleSettingsChange = (key, value) => {
    if (key === "layout") setDraftLayout(value);
    else if (key === "theme") setDraftTheme(value);
  };
  const updateAreaGuide = (key, value) => {
    setDraftAreaGuide((current) => ({ ...current, [key]: value }));
  };

  const handleDefaultGuestbookImage = (file) => {
    defaultImageRevisionRef.current += 1;
    setDefaultImageUpload(file || null);
    setDefaultImageRemoved(!file);
  };

  const handleHeaderImage = (file) => {
    headerImageRevisionRef.current += 1;
    setHeaderImageUpload(file || null);
    setHeaderImageRemoved(!file);
  };

  const resetToDefaults = () => {
    const defaultPreset = getGuideThemePreset("modern");
    setDraftTheme({
      ...DEFAULT_THEME,
      ...resolveGuideTheme(defaultPreset.id, defaultPreset.defaultAccent),
    });
    setDraftThemeId(defaultPreset.id);
    setDraftBrandAccent(defaultPreset.defaultAccent);
    setDraftLayout(defaultPreset.layoutStyle);
    setDraftAreaGuide(buildAreaGuideSettings());
    setDefaultImageUpload(null);
    setDefaultImageRemoved(false);
    setHeaderImageUpload(null);
    setHeaderImageRemoved(false);
  };

  const resetAiDefaults = () => {
    setDraftTheme((current) => ({
      ...current,
      guestAiName: DEFAULT_THEME.guestAiName,
      askRueEnabled: DEFAULT_THEME.askRueEnabled,
      aiTripPlannerEnabled: DEFAULT_THEME.aiTripPlannerEnabled,
    }));
  };

  const persistLayout = useCallback(() => {
    if (!isDirty || savedDraftRef.current === draft || queuedDraftRef.current === draft) {
      return saveQueueRef.current;
    }

    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const defaultImageRevision = defaultImageRevisionRef.current;
    const headerImageRevision = headerImageRevisionRef.current;
    const saveOperation = async () => {
      if (savedDraftRef.current === draft) return;
      setSaveStatus("saving");
      try {
        const nextGuestbook = buildGuestbookFromTheme(
          settings?.guestbook,
          draftTheme,
          draftLayout,
          {
            themeId: draftThemeId,
            brandAccent: draftBrandAccent,
          },
        );
        const nextDestination = settings?.destination !== undefined
          ? { ...(settings.destination || {}), ...draftAreaGuide }
          : null;
        const nextBranding = {
          ...(settings?.branding || {}),
          logo_url: draftLogoUrl,
        };
        let nextAttachments = allGuestbookAttachments.filter(
          (attachment) => !GUIDEBOOK_IMAGE_TYPES.has(attachment.type),
        );

        const uploadTimestamp = Date.now();
        const [defaultImageUrl, headerImageUrl] = await Promise.all([
          !defaultImageRemoved && defaultImageUpload !== null
            ? uploadFile(
                defaultImageUpload,
                `settings/guestbook/${user.user.account_id}/default-profile-${uploadTimestamp}`,
              )
            : Promise.resolve(null),
          !headerImageRemoved && headerImageUpload !== null
            ? uploadFile(
                headerImageUpload,
                `settings/guestbook/${user.user.account_id}/header-image-${uploadTimestamp}`,
              )
            : Promise.resolve(null),
        ]);

        if (!defaultImageRemoved) {
          if (defaultImageUpload !== null) {
            nextAttachments.push({
              type: "guestbook_image",
              name: "guestbook_default",
              original_name: defaultImageUpload.name || "",
              url: defaultImageUrl,
            });
          } else if (defaultGuestbookImage) {
            nextAttachments.push(defaultGuestbookImage);
          }
        }

        if (!headerImageRemoved) {
          if (headerImageUpload !== null) {
            nextAttachments.push({
              type: "guestbook_header_image",
              name: "guestbook_header_image",
              original_name: headerImageUpload.name || "",
              url: headerImageUrl,
            });
          } else if (headerImage) {
            nextAttachments.push(headerImage);
          }
        }

        nextGuestbook.attachments = nextAttachments;
        const nextBrandingAttachments = brandingAttachments.filter(
          (attachment) => !GUIDEBOOK_IMAGE_TYPES.has(attachment.type),
        );
        const hasLegacyGuidebookImages =
          nextBrandingAttachments.length !== brandingAttachments.length;

        const settingTypes = ["guestbook"];
        const updateData = { guestbook: nextGuestbook };

        const logoUrlChanged = draftLogoUrl !== savedLogoUrl;
        if (
          (hasLegacyGuidebookImages || logoUrlChanged) &&
          settings?.branding
        ) {
          settingTypes.push("branding");
          updateData.branding = {
            ...nextBranding,
            attachments: nextBrandingAttachments,
          };
        }
        if (nextDestination) {
          settingTypes.push("destination");
          updateData.destination = nextDestination;
        }

        let savedSettings;
        if (settingTypes.length > 1) {
          savedSettings = await set(settingTypes, updateData);
        } else {
          savedSettings = await set("guestbook", nextGuestbook);
        }
        // A successful API response acknowledges this exact local draft even
        // when the API canonicalizes values (for example, hex color casing).
        // A newer draft keeps a different identity and still needs saving.
        savedDraftRef.current = draft;
        const savedAttachments = Array.isArray(
          savedSettings?.guestbook?.attachments,
        )
          ? savedSettings.guestbook.attachments
          : nextAttachments;

        // Keep both settings views on the exact attachment collection that was
        // just saved. A delayed branding/guestbook response must not restore a
        // removed image or temporarily swap the two image fields.
        setSettings?.((current) => ({
          ...current,
          guestbook: {
            ...(current?.guestbook || {}),
            ...(savedSettings?.guestbook || {}),
            attachments: savedAttachments,
          },
          ...(current?.branding !== undefined
            ? {
                branding: {
                  ...(current.branding || {}),
                  ...(savedSettings?.branding || {}),
                  attachments: savedAttachments,
                },
              }
            : {}),
        }));
        if (isAreaGuideDirty) {
          onSaved && onSaved();
        }
        if (defaultImageRevisionRef.current === defaultImageRevision) {
          setDefaultImageUpload(null);
          setDefaultImageRemoved(false);
        }
        if (headerImageRevisionRef.current === headerImageRevision) {
          setHeaderImageUpload(null);
          setHeaderImageRemoved(false);
        }
        setSavedAt(Date.now());
        setSaveStatus("saved");
      } catch (error) {
        setSaveStatus("error");
        throw error;
      } finally {
        if (queuedDraftRef.current === draft) queuedDraftRef.current = null;
      }
    };

    queuedDraftRef.current = draft;
    const queuedSave = saveQueueRef.current
      .catch(() => undefined)
      .then(saveOperation);
    saveQueueRef.current = queuedSave;
    return queuedSave;
  }, [
    draft,
    allGuestbookAttachments,
    brandingAttachments,
    defaultGuestbookImage,
    defaultImageRemoved,
    defaultImageUpload,
    draftAreaGuide,
    draftBrandAccent,
    draftLayout,
    draftLogoUrl,
    draftTheme,
    draftThemeId,
    headerImage,
    headerImageRemoved,
    headerImageUpload,
    isAreaGuideDirty,
    isDirty,
    onSaved,
    savedLogoUrl,
    set,
    setSettings,
    settings?.branding,
    settings?.destination,
    settings?.guestbook,
    uploadFile,
    user?.user?.account_id,
  ]);

  persistLayoutRef.current = persistLayout;
  pendingLayoutRef.current = hasHydrated && (isDirty || saveStatus === "saving");

  const flushLayout = useCallback(async () => {
    // Edits can continue while an earlier request is pending. Keep draining the
    // newest draft before publishing or letting its editor unmount.
    let savingDraft;
    do {
      savingDraft = draftRef.current;
      await persistLayoutRef.current();
    } while (
      savingDraft !== draftRef.current ||
      (pendingLayoutRef.current && savedDraftRef.current !== draftRef.current)
    );
  }, []);

  useEffect(() => onRegisterSave?.({
    flush: flushLayout,
    hasPending: () => pendingLayoutRef.current,
  }), [flushLayout, onRegisterSave]);

  useEffect(() => {
    const beforeUnload = (event) => {
      if (!pendingLayoutRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      // Also covers callers that embed this panel without the designer shell.
      if (pendingLayoutRef.current) {
        persistLayoutRef.current().catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasHydrated || !isDirty) return undefined;

    setSaveStatus("pending");
    const hasImageChange =
      defaultImageUpload !== null ||
      defaultImageRemoved ||
      headerImageUpload !== null ||
      headerImageRemoved;
    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      persistLayout().catch(() => undefined);
    }, hasImageChange ? IMAGE_AUTOSAVE_DELAY_MS : AUTOSAVE_DELAY_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [
    defaultImageRemoved,
    defaultImageUpload,
    hasHydrated,
    headerImageRemoved,
    headerImageUpload,
    isDirty,
    persistLayout,
  ]);

  return (
    <div
      className={`guide-layout-panel${
        onboardingFocus ? ` is-onboarding-${onboardingFocus}` : ""
      }${isAiPanel ? " is-ai-panel" : ""}`}
    >
      <p className="uk-text-meta uk-margin-remove-top">
        {isAiPanel
          ? "Control the AI tools available to guests and how the assistant is presented."
          : onboardingFocus === "branding"
          ? "Add the visual identity guests will recognize."
          : "Start with a polished theme and layout, then customize only if you need to."}
      </p>

      <LayoutAccordionSection
        id="themes"
        title="Theme"
        expanded={!!expandedAccordions.themes}
        onToggle={() => toggleLayoutAccordion("themes")}
        className="guide-theme-section guide-layout-only"
        hidden={isAiPanel || onboardingFocus === "branding"}
      >
        <div className="guide-theme-grid" role="radiogroup" aria-label="Guide theme">
          {GUIDE_THEME_PRESETS.map((preset) => {
            const preview = resolveGuideTheme(preset.id, preset.defaultAccent);
            const selected = draftThemeId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`guide-theme-card${selected ? " is-selected" : ""}`}
                onClick={() => applyThemePreset(preset.id)}
              >
                <span
                  className={`guide-theme-card-preview is-${preset.id} is-${preview.blockStyle.toLowerCase()}${
                    preview.blockBorder ? " has-border" : ""
                  }`}
                  aria-hidden="true"
                  style={{
                    "--theme-preview-background": preview.pageBackgroundColor,
                    "--theme-preview-surface": preview.blockBackgroundColor,
                    "--theme-preview-heading": preview.sectionTitleColor,
                    "--theme-preview-text": preview.blockTextColor,
                    "--theme-preview-accent": preview.activeTabColor,
                    "--theme-preview-font": preview.sectionTitleFont,
                    "--theme-preview-border": preview.borderColor,
                  }}
                >
                  <span className="guide-theme-card-nav" />
                  <span className="guide-theme-card-heading">Aa</span>
                  <span className="guide-theme-card-copy">
                    <i />
                    <i />
                  </span>
                </span>
                <span className="guide-theme-card-name">{preset.name}</span>
                <span className="guide-theme-card-description">
                  {preset.description}
                </span>
                <span className="guide-theme-card-layout">
                  {preset.layoutStyle === "tabs" ? "Tabbed" : "Single page"}
                </span>
              </button>
            );
          })}
          {!draftThemeId && (
            <div className="guide-theme-card is-selected is-custom" role="radio" aria-checked="true">
              <span className="guide-theme-custom-icon" aria-hidden="true">
                <FeatherIcon name="sliders" />
              </span>
              <span className="guide-theme-card-name">Custom</span>
              <span className="guide-theme-card-description">
                Your existing or advanced settings
              </span>
            </div>
          )}
        </div>

        <div className="guide-theme-controls">
          <div className="guide-layout-field guide-theme-accent-field">
            <label className="uk-form-label" htmlFor="guideThemeAccent">
              Primary Brand Color
            </label>
            <div className="guide-theme-accent-control">
              <input
                id="guideThemeAccent"
                type="color"
                value={draftBrandAccent}
                onChange={(event) => updateBrandAccent(event.target.value)}
              />
              <span>{draftBrandAccent}</span>
            </div>
            <p className="secondary uk-text-small uk-margin-small-top uk-margin-remove-bottom">
              Used for links, buttons, icons, and selected navigation.
            </p>
          </div>
        </div>
      </LayoutAccordionSection>

      <LayoutAccordionSection
        id="header"
        title={onboardingFocus === "branding" ? "Branding" : "Header"}
        expanded={!!expandedAccordions.header}
        onToggle={() => toggleLayoutAccordion("header")}
        className="guide-branding-layout-section"
        hidden={isAiPanel}
      >

        {/*
        <div className="uk-margin-small">
          <label className="uk-form-label">
            <input
              className="uk-checkbox"
              type="checkbox"
              checked={settings?.showYourStayTab ?? true}
              onChange={(e) =>
                handleSettingsChange("showYourStayTab", e.target.checked)
              }
            />{" "}
            Show Home Guide tab
          </label>
        </div>

        <div className="uk-margin-small">
          <label className="uk-form-label">
            <input
              className="uk-checkbox"
              type="checkbox"
              checked={settings?.showAreaTab ?? true}
              onChange={(e) =>
                handleSettingsChange("showAreaTab", e.target.checked)
              }
            />{" "}
            Show Area Guide tab
          </label>
        </div>

        <div className="uk-margin-small">
          <label className="uk-form-label">
            <input
              className="uk-checkbox"
              type="checkbox"
              checked={settings?.showExtrasTab ?? true}
              onChange={(e) =>
                handleSettingsChange("showExtrasTab", e.target.checked)
              }
            />{" "}
            Show Extras tab
          </label>
        </div>
        */}
        <div
          className="guide-branding-controls"
          hidden={onboardingFocus === "layout"}
        >
          {onboardingFocus !== "branding" && (
            <>
              <ToggleField
                id="guideLayoutHideLogo"
                label="Hide Logo"
                checked={!!theme.hideGuideLogo}
                onChange={() => toggleThemeBool("hideGuideLogo")}
              />
              <p className="secondary uk-text-small uk-margin-small-top uk-margin-small-bottom">
                Add your company logo in{" "}
                <a href="/settings/branding">Settings → Branding</a>.
              </p>

              <div className="guide-layout-field">
                <label className="uk-form-label" htmlFor="guideLayoutLogoLink">
                  Guide Logo Link
                </label>
                <input
                  id="guideLayoutLogoLink"
                  className="uk-input"
                  type="url"
                  value={draftLogoUrl}
                  disabled={!!theme.hideGuideLogo}
                  onChange={(event) => setDraftLogoUrl(event.target.value)}
                  placeholder="https://example.com"
                />
                <p className="secondary uk-text-small uk-margin-remove">
                  {theme.hideGuideLogo
                    ? "Show the logo to edit where it links."
                    : "Guests go to this page when they click the logo."}
                </p>
              </div>
            </>
          )}

          {/* Temporarily hidden while header image behavior is being revised. */}
          <div className="guide-layout-field" hidden>
            <ImageInput
              compact
              label="Header Image"
              attachment={headerImageRemoved ? null : headerImage}
              handler={handleHeaderImage}
            />
            <p className="secondary uk-text-small uk-margin-remove">
              Behind logo and menu · 1600 × 400 px · JPG/PNG · 5 MB max
            </p>
          </div>

          <div className="guide-layout-field" hidden>
            <label className="uk-form-label" htmlFor="guideHeaderImagePosition">
              Header Image Position
            </label>
            <select
              id="guideHeaderImagePosition"
              className="uk-select"
              value={theme.headerImagePosition}
              onChange={(event) =>
                updateTheme("headerImagePosition", event.target.value)
              }
            >
              {HEADER_IMAGE_POSITION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="guide-layout-field">
            <ImageInput
              compact
              label="Default Profile Photo"
              attachment={defaultImageRemoved ? null : defaultGuestbookImage}
              handler={handleDefaultGuestbookImage}
            />
            <p className="secondary uk-text-small uk-margin-remove">
              800 px minimum · 16:9 · JPG/PNG · 5 MB max
            </p>
          </div>
        </div>

        <div
          className="guide-layout-field guide-layout-core-controls guide-layout-style-field"
          hidden={onboardingFocus === "branding"}
        >
          <label className="uk-form-label">Layout Style</label>
          <select
            className="uk-select"
            value={draftLayout}
            onChange={(e) => handleSettingsChange("layout", e.target.value)}
          >
            <option value="tabs">Tabs (Default)</option>
            <option value="single-scroll">Single Scroll</option>
          </select>
        </div>

        <div
          className="guide-layout-field guide-layout-core-controls"
          hidden={onboardingFocus === "branding"}
        >
          <label className="uk-form-label">Guestbook Headline</label>
          <input
            className="uk-input"
            type="text"
            value={theme.guestbookHeadline}
            onChange={(e) => updateTheme("guestbookHeadline", e.target.value)}
            placeholder="Welcome To Your Vacation"
          />
        </div>

        <div
          className="uk-grid uk-grid-small guide-layout-row guide-layout-core-controls"
          hidden={onboardingFocus === "branding"}
        >
          <div className="uk-width-1-2">
            <label className="uk-form-label">Your Stay Section Title</label>
            <input
              className="uk-input"
              type="text"
              value={theme.yourStayLabel}
              onChange={(e) => updateTheme("yourStayLabel", e.target.value)}
              placeholder={DEFAULT_THEME.yourStayLabel}
            />
          </div>
          <div className="uk-width-1-2">
            <label className="uk-form-label">Home Guide Section Title</label>
            <input
              className="uk-input"
              type="text"
              value={theme.homeGuideLabel}
              onChange={(e) => updateTheme("homeGuideLabel", e.target.value)}
              placeholder={DEFAULT_THEME.homeGuideLabel}
            />
          </div>
        </div>

        <div
          className="uk-grid uk-grid-small guide-layout-row guide-layout-core-controls"
          hidden={onboardingFocus === "branding"}
        >
          <div className="uk-width-1-2">
            <label className="uk-form-label">Area Guide Section Title</label>
            <input
              className="uk-input"
              type="text"
              value={theme.areaGuideLabel}
              onChange={(e) => updateTheme("areaGuideLabel", e.target.value)}
              placeholder={DEFAULT_THEME.areaGuideLabel}
            />
          </div>
          <div className="uk-width-1-2">
            <label className="uk-form-label">Extras Guide Section Title</label>
            <input
              className="uk-input"
              type="text"
              value={theme.extrasGuideLabel}
              onChange={(e) => updateTheme("extrasGuideLabel", e.target.value)}
              placeholder={DEFAULT_THEME.extrasGuideLabel}
            />
          </div>
        </div>
      </LayoutAccordionSection>

      <div
        className="guide-layout-advanced-label guide-layout-only"
        hidden={isAiPanel || onboardingFocus === "branding"}
      >
        Advanced customization
        <span>Changing these controls creates a custom theme.</span>
      </div>

      <LayoutAccordionSection
        id="colors"
        title="Colors"
        expanded={!!expandedAccordions.colors}
        onToggle={() => toggleLayoutAccordion("colors")}
        className="guide-branding-only"
        hidden={isAiPanel || onboardingFocus === "layout"}
      >
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <ColorField
              label="Page Background Color"
              value={theme.pageBackgroundColor}
              onChange={(value) => updateTheme("pageBackgroundColor", value)}
              onReset={() => resetThemeValue("pageBackgroundColor")}
              fallback={DEFAULT_THEME.pageBackgroundColor}
            />
          </div>

          <div className="uk-width-1-2">
            <ColorField
              label="Active Tab"
              value={theme.activeTabColor}
              onChange={(value) => updateTheme("activeTabColor", value)}
              onReset={() => resetThemeValue("activeTabColor")}
              fallback={DEFAULT_THEME.activeTabColor}
            />
          </div>
        </div>

        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <ColorField
              label="Tab Text"
              value={theme.tabTextColor}
              onChange={(value) => updateTheme("tabTextColor", value)}
              onReset={() => resetThemeValue("tabTextColor")}
              fallback={DEFAULT_THEME.tabTextColor}
            />
          </div>

          <div className="uk-width-1-2">
            <ColorField
              label="Header Background Color"
              value={theme.headerBackgroundColor}
              onChange={(value) => updateTheme("headerBackgroundColor", value)}
              onReset={() => resetThemeValue("headerBackgroundColor")}
              fallback={DEFAULT_THEME.headerBackgroundColor}
              placeholder={DEFAULT_THEME.headerBackgroundColor}
            />
          </div>
        </div>

        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <ColorField
              label="Distance Color"
              value={theme.distanceColor}
              onChange={(value) => updateTheme("distanceColor", value)}
              onReset={() => resetThemeValue("distanceColor")}
              fallback={DEFAULT_THEME.distanceColor}
            />
          </div>
        </div>
      </LayoutAccordionSection>

      <LayoutAccordionSection
        id="typography"
        title="Typography"
        expanded={!!expandedAccordions.typography}
        onToggle={() => toggleLayoutAccordion("typography")}
        className="guide-layout-only"
        hidden={isAiPanel || onboardingFocus === "branding"}
      >
        <div className="guide-layout-typography-group">
          <div className="guide-layout-section-header">
            <h4 className="guide-layout-section-title">Guide Title</h4>
            <FontPreview
              fontFamily={theme.guideTitleFont}
              fontSize={theme.guideTitleFontSize}
              fontWeight={theme.guideTitleFontWeight}
              color={theme.guideTitleColor}
            />
          </div>

          <div className="uk-grid uk-grid-small guide-layout-row">
            <div className="uk-width-1-2">
              <label className="uk-form-label">Font</label>
              <select
                className="uk-select"
                value={theme.guideTitleFont}
                onChange={(e) => updateTheme("guideTitleFont", e.target.value)}
              >
                {FONT_OPTIONS.map((font) => (
                  <option
                    key={font.value}
                    value={font.value}
                    style={{ fontFamily: font.value }}
                  >
                    {font.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="uk-width-1-2">
              <ColorField
                label="Color"
                value={theme.guideTitleColor}
                onChange={(value) => updateTheme("guideTitleColor", value)}
                onReset={() => resetThemeValue("guideTitleColor")}
                fallback={DEFAULT_THEME.guideTitleColor}
              />
            </div>
          </div>

          <div className="uk-grid uk-grid-small guide-layout-row">
            <div className="uk-width-1-2">
              <label className="uk-form-label">Size</label>
              <select
                className="uk-select"
                value={theme.guideTitleFontSize}
                onChange={(e) =>
                  updateTheme("guideTitleFontSize", e.target.value)
                }
              >
                {SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="uk-width-1-2">
              <label className="uk-form-label">Weight</label>
              <select
                className="uk-select"
                value={theme.guideTitleFontWeight}
                onChange={(e) =>
                  updateTheme("guideTitleFontWeight", e.target.value)
                }
              >
                {WEIGHT_OPTIONS.map((weight) => (
                  <option key={weight.value} value={weight.value}>
                    {weight.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="guide-layout-typography-group">
        <div className="guide-layout-section-header">
          <h4 className="guide-layout-section-title">Section Titles</h4>
          <FontPreview
            fontFamily={theme.sectionTitleFont}
            fontSize={theme.sectionTitleFontSize}
            fontWeight={theme.sectionTitleFontWeight}
            color={theme.sectionTitleColor}
          />
        </div>

        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Font</label>
            <select
              className="uk-select"
              value={theme.sectionTitleFont}
              onChange={(e) =>
                updateTheme("sectionTitleFont", e.target.value)
              }
            >
              {FONT_OPTIONS.map((f) => (
                <option
                  key={f.value}
                  value={f.value}
                  style={{ fontFamily: f.value }}
                >
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <ColorField
              label="Color"
              value={theme.sectionTitleColor}
              onChange={(value) => updateTheme("sectionTitleColor", value)}
              onReset={() => resetThemeValue("sectionTitleColor")}
              fallback={DEFAULT_THEME.sectionTitleColor}
            />
          </div>
        </div>

        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Size</label>
            <select
              className="uk-select"
              value={theme.sectionTitleFontSize}
              onChange={(e) =>
                updateTheme("sectionTitleFontSize", e.target.value)
              }
            >
              {SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <label className="uk-form-label">Weight</label>
            <select
              className="uk-select"
              value={theme.sectionTitleFontWeight}
              onChange={(e) =>
                updateTheme("sectionTitleFontWeight", e.target.value)
              }
            >
              {WEIGHT_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        </div>

        <div className="guide-layout-typography-group">
        <div className="guide-layout-section-header">
          <h4 className="guide-layout-section-title">Section Intro Text</h4>
          <FontPreview
            fontFamily={theme.introTextFont}
            fontSize={theme.introTextFontSize}
            fontWeight={theme.introTextFontWeight}
            color={theme.introTextColor}
          />
        </div>
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-1">
            <label className="uk-form-label">Your Stay Intro Text</label>
            <textarea
              className="uk-textarea guide-layout-intro-input"
              rows="3"
              maxLength={500}
              value={theme.yourStayIntroText}
              onChange={(e) => updateTheme("yourStayIntroText", e.target.value)}
              placeholder="Welcome guests and introduce their stay."
            />
          </div>
          <div className="uk-width-1-1 uk-margin-small-top">
            <label className="uk-form-label">Home Guide Intro Text</label>
            <textarea
              className="uk-textarea guide-layout-intro-input"
              rows="3"
              maxLength={500}
              value={theme.homeGuideIntroText}
              onChange={(e) => updateTheme("homeGuideIntroText", e.target.value)}
              placeholder="Introduce your home information."
            />
          </div>
        </div>
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-1">
            <label className="uk-form-label">Area Guide Intro Text</label>
            <textarea
              className="uk-textarea guide-layout-intro-input"
              rows="3"
              maxLength={500}
              value={theme.areaGuideIntroText}
              onChange={(e) => updateTheme("areaGuideIntroText", e.target.value)}
              placeholder="Introduce the local area."
            />
          </div>
          <div className="uk-width-1-1 uk-margin-small-top">
            <label className="uk-form-label">Extras Intro Text</label>
            <textarea
              className="uk-textarea guide-layout-intro-input"
              rows="3"
              maxLength={500}
              value={theme.extrasGuideIntroText}
              onChange={(e) => updateTheme("extrasGuideIntroText", e.target.value)}
              placeholder="Introduce available extras."
            />
          </div>
        </div>
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Font</label>
            <select
              className="uk-select"
              value={theme.introTextFont}
              onChange={(e) =>
                updateTheme("introTextFont", e.target.value)
              }
            >
              {FONT_OPTIONS.map((f) => (
                <option
                  key={f.value}
                  value={f.value}
                  style={{ fontFamily: f.value }}
                >
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <ColorField
              label="Color"
              value={theme.introTextColor}
              onChange={(value) => updateTheme("introTextColor", value)}
              onReset={() => resetThemeValue("introTextColor")}
              fallback={DEFAULT_THEME.introTextColor}
            />
          </div>
        </div>
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Size</label>
            <select
              className="uk-select"
              value={theme.introTextFontSize}
              onChange={(e) =>
                updateTheme("introTextFontSize", e.target.value)
              }
            >
              {SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <label className="uk-form-label">Weight</label>
            <select
              className="uk-select"
              value={theme.introTextFontWeight}
              onChange={(e) =>
                updateTheme("introTextFontWeight", e.target.value)
              }
            >
              {WEIGHT_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        </div>

        <div className="guide-layout-typography-group">
        <div className="guide-layout-section-header">
          <h4 className="guide-layout-section-title">Block Title</h4>
          <FontPreview
            fontFamily={theme.blockTitleFont}
            fontSize={theme.blockTitleFontSize}
            fontWeight={theme.blockTitleFontWeight}
            color={theme.blockTitleColor}
          />
        </div>

        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Font</label>
            <select
              className="uk-select"
              value={theme.blockTitleFont}
              onChange={(e) =>
                updateTheme("blockTitleFont", e.target.value)
              }
            >
              {FONT_OPTIONS.map((f) => (
                <option
                  key={f.value}
                  value={f.value}
                  style={{ fontFamily: f.value }}
                >
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <ColorField
              label="Color"
              value={theme.blockTitleColor}
              onChange={(value) => updateTheme("blockTitleColor", value)}
              onReset={() => resetThemeValue("blockTitleColor")}
              fallback={DEFAULT_THEME.blockTitleColor}
            />
          </div>
        </div>

        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Size</label>
            <select
              className="uk-select"
              value={theme.blockTitleFontSize}
              onChange={(e) =>
                updateTheme("blockTitleFontSize", e.target.value)
              }
            >
              {SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <label className="uk-form-label">Weight</label>
            <select
              className="uk-select"
              value={theme.blockTitleFontWeight}
              onChange={(e) =>
                updateTheme("blockTitleFontWeight", e.target.value)
              }
            >
              {WEIGHT_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        </div>

        <div className="guide-layout-typography-group">
        <div className="guide-layout-section-header">
          <h4 className="guide-layout-section-title">Block Text</h4>
          <FontPreview
            fontFamily={theme.blockTextFont}
            fontSize={theme.blockTextFontSize}
            fontWeight={theme.blockTextFontWeight}
            color={theme.blockTextColor}
          />
        </div>
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Font</label>
            <select
              className="uk-select"
              value={theme.blockTextFont}
              onChange={(e) =>
                updateTheme("blockTextFont", e.target.value)
              }
            >
              {FONT_OPTIONS.map((f) => (
                <option
                  key={f.value}
                  value={f.value}
                  style={{ fontFamily: f.value }}
                >
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <ColorField
              label="Color"
              value={theme.blockTextColor}
              onChange={(value) => updateTheme("blockTextColor", value)}
              onReset={() => resetThemeValue("blockTextColor")}
              fallback={DEFAULT_THEME.blockTextColor}
            />
          </div>
        </div>
        <div className="uk-grid uk-grid-small guide-layout-row">
          <div className="uk-width-1-2">
            <label className="uk-form-label">Size</label>
            <select
              className="uk-select"
              value={theme.blockTextFontSize}
              onChange={(e) =>
                updateTheme("blockTextFontSize", e.target.value)
              }
            >
              {SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-2">
            <label className="uk-form-label">Weight</label>
            <select
              className="uk-select"
              value={theme.blockTextFontWeight}
              onChange={(e) =>
                updateTheme("blockTextFontWeight", e.target.value)
              }
            >
              {WEIGHT_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        </div>
      </LayoutAccordionSection>

      <LayoutAccordionSection
        id="block"
        title="Block Appearance"
        expanded={!!expandedAccordions.block}
        onToggle={() => toggleLayoutAccordion("block")}
        className="guide-layout-only"
        hidden={isAiPanel || onboardingFocus === "branding"}
      >
        <div className="guide-layout-field">
          <label className="uk-form-label">Style</label>
          <select
            className="uk-select"
            value={theme.blockStyle}
            onChange={(e) => updateTheme("blockStyle", e.target.value)}
          >
            <option value="Collapsible">Collapsible (accordion)</option>
            <option value="Inline">Inline (always open)</option>
          </select>
        </div>

        <div className="guide-layout-field">
          <ColorField
            label="Block Background Color"
            value={theme.blockBackgroundColor}
            onChange={(value) => updateTheme("blockBackgroundColor", value)}
            onReset={() => resetThemeValue("blockBackgroundColor")}
            fallback={DEFAULT_THEME.blockBackgroundColor}
          />
        </div>

        <ToggleField
          id="guideLayoutBlockBorder"
          label="Show block border"
          checked={!!theme.blockBorder}
          onChange={() => toggleThemeBool("blockBorder")}
        />

        <ToggleField
          id="guideLayoutShowIcons"
          label="Show icons next to block titles"
          checked={!!theme.showIcons}
          onChange={() => toggleThemeBool("showIcons")}
        />

        {theme.showIcons && (
          <div className="guide-layout-field guide-layout-indent">
            <ColorField
              label="Icon Color"
              value={theme.iconColor}
              onChange={(value) => updateTheme("iconColor", value)}
              onReset={() => resetThemeValue("iconColor")}
              fallback={DEFAULT_THEME.iconColor}
            />
          </div>
        )}
      </LayoutAccordionSection>

      <LayoutAccordionSection
        id="area"
        title="Area Guide"
        expanded={!!expandedAccordions.area}
        onToggle={() => toggleLayoutAccordion("area")}
        className="guide-layout-only"
        hidden={isAiPanel || onboardingFocus === "branding"}
      >
        <div className="guide-layout-field">
          <label className="uk-form-label" htmlFor="areaGuideTipTitle">
            Tip Title
          </label>
          <input
            id="areaGuideTipTitle"
            className="uk-input"
            type="text"
            value={draftAreaGuide.tip_title || ""}
            onChange={(event) =>
              updateAreaGuide("tip_title", event.target.value)
            }
          />
        </div>

        <ToggleField
          id="layoutDistanceToPlaces"
          label="Distance to Places"
          checked={!!draftAreaGuide.show_guide_distance}
          onChange={() =>
            updateAreaGuide(
              "show_guide_distance",
              !draftAreaGuide.show_guide_distance,
            )
          }
        />

        <ToggleField
          id="layoutPropertyIconOnMap"
          label="Property Icon on Map"
          checked={!!draftAreaGuide.show_guide_home}
          onChange={() =>
            updateAreaGuide("show_guide_home", !draftAreaGuide.show_guide_home)
          }
        />
      </LayoutAccordionSection>

      {isAiPanel && !onboardingFocus && (
        <section
          className="guide-ai-settings"
          aria-label="AI Assistant settings"
        >
          <div className="guide-ai-feature-group guide-ai-guest-assistant">
            <div className="guide-ai-feature-heading">
              <h4>Guest Assistant</h4>
              <p>Get quick answers from this guestbook.</p>
            </div>

            <ToggleField
              id="layoutAskRueEnabled"
              label="Enable Guest Assistant"
              checked={!!theme.askRueEnabled}
              onChange={() => toggleThemeBool("askRueEnabled")}
            />

            {theme.askRueEnabled && (
              <div className="guide-layout-field guide-layout-indent">
                <label className="uk-form-label" htmlFor="layoutGuestAiName">
                  Guest Assistant Name
                </label>
                <input
                  id="layoutGuestAiName"
                  className="uk-input"
                  type="text"
                  value={theme.guestAiName}
                  maxLength={40}
                  onChange={(e) => updateTheme("guestAiName", e.target.value)}
                  placeholder={DEFAULT_THEME.guestAiName}
                />
                <p className="secondary uk-text-small uk-margin-remove">
                  Guests see this name in the guide assistant.
                </p>
              </div>
            )}
          </div>

          <div className="guide-ai-trip-planner">
            <ToggleField
              id="layoutAiTripPlannerEnabled"
              label="Trip Planner"
              checked={!!theme.aiTripPlannerEnabled}
              onChange={() => toggleThemeBool("aiTripPlannerEnabled")}
            />
          </div>
        </section>
      )}

      <div className="guide-layout-actionbar">
        <div className="guide-layout-actionbar-meta">
          <div
            className="guide-layout-actionbar-status"
            role="status"
            aria-live="polite"
          >
            {saveStatus === "saving" || saveStatus === "pending" ? (
              <span className="guide-layout-status-dirty">Saving…</span>
            ) : saveStatus === "error" ? (
              <span className="guide-layout-status-dirty">
                Unable to save draft
              </span>
            ) : savedAt || saveStatus === "saved" ? (
              <span className="guide-layout-status-saved">Draft saved</span>
            ) : (
              <span className="uk-text-muted">No changes</span>
            )}
          </div>
          <button
            type="button"
            className="uk-button uk-button-text guide-layout-reset-button"
            onClick={isAiPanel ? resetAiDefaults : resetToDefaults}
            disabled={loading}
          >
            {isAiPanel ? "Reset AI Defaults" : "Reset to Defaults"}
          </button>
        </div>
        {!hideRevisionControls && (
          <RevisionControls
            onBeforeSave={flushLayout}
            disabled={!settings?.guestbook}
            publishLabel={isAiPanel ? "Publish AI Settings" : "Publish Layout"}
            publishTitle={isAiPanel ? "Publish AI Settings" : "Publish Layout"}
            publishMessage="Publish the current layout, design, and AI assistant settings to every guest?"
            revisionLabel={
              isAiPanel
                ? "Published AI settings from designer"
                : "Published layout from designer"
            }
          />
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Lato:wght@400;700&family=Oswald:wght@400;700&family=Poppins:wght@400;700&family=Roboto:wght@400;500;700&display=swap');
        .guide-layout-panel {
          display: flex;
          flex-direction: column;
          min-height: 100%;
          padding: 4px 0 0;
        }
        .guide-layout-panel .guide-ai-guest-assistant {
          margin-top: 20px;
        }
        .guide-layout-panel .guide-ai-trip-planner {
          margin-top: 20px;
        }
        .guide-layout-panel .guide-ai-feature-heading {
          margin-bottom: 14px;
        }
        .guide-layout-panel .guide-ai-feature-heading h4 {
          margin: 0 0 4px;
          color: #243e56;
          font-size: 15px;
          font-weight: 700;
        }
        .guide-layout-panel .guide-ai-feature-heading p {
          margin: 0;
          color: #6b7c93;
          font-size: 13px;
          line-height: 1.45;
        }
        .guide-layout-panel .guide-theme-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .guide-layout-panel .guide-theme-card {
          display: flex;
          min-width: 0;
          padding: 8px;
          border: 1px solid #dbe3eb;
          border-radius: 10px;
          background: #ffffff;
          color: #243e56;
          text-align: left;
          cursor: pointer;
          flex-direction: column;
          transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
        }
        .guide-layout-panel button.guide-theme-card:hover {
          border-color: #86b6d7;
          transform: translateY(-1px);
        }
        .guide-layout-panel .guide-theme-card.is-selected {
          border-color: #2e83bd;
          box-shadow: 0 0 0 2px rgba(46, 131, 189, 0.14);
        }
        .guide-layout-panel .guide-theme-card:focus-visible {
          outline: 2px solid #3896db;
          outline-offset: 2px;
        }
        .guide-layout-panel .guide-theme-card-preview {
          position: relative;
          display: block;
          width: 100%;
          height: 66px;
          margin-bottom: 8px;
          overflow: hidden;
          border-radius: 6px;
          background: var(--theme-preview-background);
        }
        .guide-layout-panel .guide-theme-card-heading {
          position: absolute;
          top: 10px;
          left: 10px;
          color: var(--theme-preview-heading);
          font-family: var(--theme-preview-font);
          font-size: 16px;
          font-weight: 700;
          line-height: 1;
        }
        .guide-layout-panel .guide-theme-card-nav {
          position: absolute;
          top: 0;
          right: 0;
          width: 25%;
          height: 100%;
          background: var(--theme-preview-accent);
        }
        .guide-layout-panel .guide-theme-card-copy {
          position: absolute;
          top: 28px;
          right: 31%;
          bottom: 9px;
          left: 9px;
          display: flex;
          padding: 8px;
          border: 0;
          border-radius: 5px;
          background: var(--theme-preview-surface);
          flex-direction: column;
          gap: 5px;
        }
        .guide-layout-panel .guide-theme-card-preview.has-border .guide-theme-card-copy {
          border: 1px solid var(--theme-preview-border);
        }
        .guide-layout-panel .guide-theme-card-preview.is-inline .guide-theme-card-copy {
          border-radius: 0;
          border-right: 0;
          border-left: 0;
        }
        .guide-layout-panel .guide-theme-card-preview.is-modern .guide-theme-card-nav {
          top: 0;
          right: 0;
          width: 100%;
          height: 6px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-modern .guide-theme-card-heading {
          top: 12px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-modern .guide-theme-card-copy {
          right: 9px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-coastal .guide-theme-card-nav {
          width: 7px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-coastal .guide-theme-card-heading {
          left: 14px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-coastal .guide-theme-card-copy {
          right: 9px;
          left: 13px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-boutique .guide-theme-card-nav {
          top: auto;
          bottom: 8px;
          right: 9px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
        }
        .guide-layout-panel .guide-theme-card-preview.is-classic .guide-theme-card-nav {
          top: 8px;
          right: 8px;
          width: 22%;
          height: 5px;
          border-radius: 999px;
        }
        .guide-layout-panel .guide-theme-card-preview.is-classic .guide-theme-card-copy {
          right: 9px;
        }
        .guide-layout-panel .guide-theme-card-copy i {
          display: block;
          width: 86%;
          height: 4px;
          border-radius: 2px;
          background: var(--theme-preview-text);
          opacity: 0.55;
        }
        .guide-layout-panel .guide-theme-card-copy i:last-child {
          width: 62%;
        }
        .guide-layout-panel .guide-theme-card-name {
          font-size: 13px;
          font-weight: 700;
          line-height: 1.3;
        }
        .guide-layout-panel .guide-theme-card-description {
          margin-top: 2px;
          color: #6b7c93;
          font-size: 11px;
          line-height: 1.35;
        }
        .guide-layout-panel .guide-theme-card-layout {
          align-self: flex-start;
          margin-top: 6px;
          padding: 2px 6px;
          border-radius: 999px;
          background: #eef3f7;
          color: #50647a;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.03em;
          line-height: 1.4;
          text-transform: uppercase;
        }
        .guide-layout-panel .guide-theme-card.is-custom {
          min-height: 112px;
          justify-content: center;
          cursor: default;
        }
        .guide-layout-panel .guide-theme-custom-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          margin-bottom: 8px;
          border-radius: 50%;
          background: #eef4f8;
          color: #365d7d;
        }
        .guide-layout-panel .guide-theme-custom-icon .feather-icon {
          width: 17px;
          height: 17px;
        }
        .guide-layout-panel .guide-theme-controls {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          gap: 14px;
          margin-top: 18px;
          padding-top: 16px;
          border-top: 1px solid #eef2f5;
        }
        .guide-layout-panel .guide-theme-accent-control {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 40px;
          color: #44566c;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12px;
        }
        .guide-layout-panel .guide-theme-accent-control input {
          width: 42px;
          height: 36px;
          padding: 2px;
          border: 1px solid #d9e1e8;
          border-radius: 7px;
          background: #ffffff;
          cursor: pointer;
        }
        .guide-layout-panel .guide-layout-advanced-label {
          display: flex;
          margin-top: 12px;
          padding-top: 18px;
          border-top: 1px solid #dfe6ed;
          color: #44566c;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.02em;
          flex-direction: column;
          gap: 2px;
          text-transform: uppercase;
        }
        .guide-layout-panel .guide-layout-advanced-label span {
          color: #8794a5;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0;
          text-transform: none;
        }
        .guide-layout-panel .guide-layout-section {
          padding: 16px 0;
          border-top: 1px solid #eef2f5;
        }
        .guide-layout-panel .guide-layout-section:first-of-type {
          border-top: none;
          padding: 8px 0;
        }
        .guide-layout-panel.is-onboarding-branding .guide-layout-core-controls,
        .guide-layout-panel.is-onboarding-branding .guide-layout-only,
        .guide-layout-panel.is-onboarding-layout .guide-branding-controls,
        .guide-layout-panel.is-onboarding-layout .guide-branding-only {
          display: none;
        }
        .guide-layout-panel .guide-layout-section-title {
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #6b7c93;
          margin: 0 0 12px;
        }
        .guide-layout-panel .guide-layout-accordion-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0;
          border: 0;
          background: transparent;
          color: inherit;
          text-align: left;
          cursor: pointer;
        }
        .guide-layout-panel .guide-layout-accordion-trigger:focus-visible {
          outline: 2px solid #3896db;
          outline-offset: 4px;
          border-radius: 2px;
        }
        .guide-layout-panel
          .guide-layout-accordion-trigger
          .guide-layout-section-title {
          margin: 0;
        }
        .guide-layout-panel .guide-layout-accordion-trigger .feather-icon {
          width: 17px;
          height: 17px;
          flex: 0 0 auto;
          color: #6b7c93 !important;
          stroke-width: 2;
        }
        .guide-layout-panel .guide-layout-accordion-content {
          padding-top: 14px;
        }
        .guide-layout-panel .guide-layout-typography-group {
          padding: 16px 0;
          border-top: 1px solid #eef2f5;
        }
        .guide-layout-panel .guide-layout-typography-group:first-child {
          padding-top: 0;
          border-top: 0;
        }
        .guide-layout-panel .guide-layout-typography-group:last-child {
          padding-bottom: 0;
        }
        .guide-layout-panel .guide-layout-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin: 0 0 12px;
        }
        .guide-layout-panel .guide-layout-section-header
          .guide-layout-section-title {
          margin: 0;
        }
        .guide-layout-panel .guide-layout-aa {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 56px;
          height: 40px;
          padding: 0 10px;
          background: #ffffff;
          border: 1px solid #e3e8ef;
          border-radius: 6px;
          line-height: 1;
          overflow: hidden;
          white-space: nowrap;
          font-family: var(--guide-layout-preview-font-family) !important;
          font-size: var(--guide-layout-preview-font-size) !important;
          font-weight: var(--guide-layout-preview-font-weight) !important;
          color: var(--guide-layout-preview-color) !important;
        }
        .guide-layout-panel .guide-layout-field {
          margin-bottom: 14px;
        }
        .guide-layout-panel .guide-layout-field:last-child {
          margin-bottom: 0;
        }
        .guide-layout-panel .guide-layout-style-field {
          margin-top: 18px;
        }
        .guide-layout-panel .guide-layout-intro-input {
          min-height: 82px;
          border-radius: 4px;
          resize: vertical;
        }
        .guide-layout-panel .guide-layout-row {
          margin-bottom: 12px;
        }
        .guide-layout-panel .guide-layout-row:last-child {
          margin-bottom: 0;
        }
        .guide-layout-panel .guide-layout-row .uk-form-label {
          margin-bottom: 4px;
        }
        .guide-layout-panel .guide-color-field {
          width: 240px;
          max-width: 100%;
        }
        .guide-layout-panel .guide-color-field > .uk-form-label {
          display: block;
          margin-bottom: 5px;
          color: #44566c;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.35;
        }
        .guide-layout-panel .guide-color-field .setting-color-box {
          width: 100%;
          height: 40px;
          overflow: hidden;
          border: 1px solid #d9e1e8;
          border-radius: 9px;
          background: #ffffff;
          box-shadow: 0 1px 2px rgba(36, 62, 86, 0.04);
          transition:
            border-color 150ms ease,
            box-shadow 150ms ease;
        }
        .guide-layout-panel .guide-color-field .setting-color-box:focus-within {
          border-color: #3896db;
          box-shadow: 0 0 0 3px rgba(56, 150, 219, 0.14);
        }
        .guide-layout-panel .guide-color-control {
          display: flex;
          align-items: center;
          width: 100%;
          height: 38px;
        }
        .guide-layout-panel .guide-color-field .rbr-color-preview {
          position: relative;
          flex: 0 0 38px;
          width: 38px;
          min-width: 38px;
          max-width: 38px;
          height: 38px;
          overflow: hidden;
          border: 0;
          border-right: 1px solid #e2e8ee;
        }
        .guide-layout-panel .guide-color-field .rbr-color-preview::after {
          position: absolute;
          inset: 7px;
          border: 1px solid rgba(36, 62, 86, 0.18);
          border-radius: 6px;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
          content: "";
          pointer-events: none;
        }
        .guide-layout-panel .guide-color-field .rbr-color-preview input {
          width: 100%;
          height: 100%;
          padding: 0;
          border: 0;
          cursor: pointer;
          opacity: 0;
        }
        .guide-layout-panel .guide-color-field .setting-color-value {
          flex: 1 1 auto;
          width: 0;
          min-width: 0;
          height: 38px;
          padding: 0 11px;
          border: 0;
          background: transparent;
          color: #243e56;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.02em;
          outline: none;
        }
        .guide-layout-panel .guide-color-field .resetOldColor {
          min-height: auto;
          margin-top: 4px;
          padding: 0;
          color: #6b7c93;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.4;
          text-transform: none;
        }
        .guide-layout-panel .guide-color-field .resetOldColor:hover,
        .guide-layout-panel .guide-color-field .resetOldColor:focus {
          color: #3896db;
        }
        .guide-layout-panel .uk-input,
        .guide-layout-panel .uk-select,
        .guide-layout-panel .setting-color-value {
          border-radius: 4px;
        }
        .guide-layout-panel .guide-layout-toggle-label {
          font-weight: normal;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .guide-layout-panel .guide-layout-toggle-label .setting-switch {
          flex: 0 0 29px;
          margin: 0;
        }
        .guide-layout-panel .guide-layout-indent {
          margin-left: 24px;
        }
        /* Sticky action bar */
        .guide-layout-actionbar {
          position: sticky;
          bottom: -16px;
          left: 0;
          right: 0;
          margin: auto -16px -16px;
          padding: 12px 16px;
          background: #fff;
          border-top: 1px solid #e3e8ef;
          display: flex;
          align-items: stretch;
          flex-direction: column;
          gap: 12px;
          z-index: 2;
        }
        .guide-layout-actionbar-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .guide-layout-actionbar-status { font-size: 12px; }
        .guide-layout-status-dirty { color: #b25a00; font-weight: 600; }
        .guide-layout-status-saved { color: #2f8a4a; font-weight: 600; }
        .guide-layout-reset-button {
          min-height: auto;
          padding: 0;
          font-size: 12px;
          line-height: 1.4;
          text-transform: none;
        }
      `}</style>
    </div>
  );
};

export default BuilderLayoutPanel;
